/**
 * Top-level mode switching for the workspace.
 *
 * Perspectives share the same workspace data:
 *   - "board"   — the existing infinite canvas (default).
 *   - "types"   — a library browser for declared kinds.
 *   - "process" — process swimlane view.
 *   - "docs"    — the CM6 markdown document surface (a singleton `doc` tab,
 *                 re-pointed per document for fast switching).
 *
 * Mode is now a DERIVED mirror of the active tab (see the universal-tab model
 * in `tabs.ts`): switching mode activates/opens a tab of that kind, and the tab
 * snapshot is what persists across reloads. There is no longer a URL-hash
 * binding for mode — tabs own "which perspective", so a stale `#mode=…` would
 * just fight the restored tab. This hook is a thin store-backed accessor.
 */
import { useCallback } from "react";
import {
  useActiveTabId,
  useTabs,
  type WorkspaceMode,
  type WorkspaceStore,
} from "../workbench/WorkspaceStore.js";
import { modeForTab } from "../workbench/tabs.js";

export type Mode = WorkspaceMode;

/**
 * The active perspective + a switcher. There is NO stored `mode` — the
 * perspective is purely DERIVED from the active tab's kind (`modeForTab`), and
 * `setMode` just activates/opens a tab of that kind. So the perspective can
 * never drift from the tab the user is actually looking at.
 */
export function useMode(
  store: WorkspaceStore,
): { mode: Mode; setMode: (m: Mode) => void } {
  const tabs = useTabs(store);
  const activeTabId = useActiveTabId(store);
  const mode = modeForTab(tabs.find((t) => t.id === activeTabId));
  const setMode = useCallback((next: Mode) => store.setMode(next), [store]);
  return { mode, setMode };
}
